import { ErrorLevel, globalRef, logDebug, logWarn, ReportableError } from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { getLocale } from '@amzn/bf-i18n'
import { loadDeviceInfo } from '@/device/device'
import { getAnonSessionId, getAuth, getAuthToken } from '@/auth/authRef'
import { featureEnabled } from '@/core/featureGate'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql/language'
import {
    deriveGqlCacheKey as deriveGqlCacheKeyGeneric,
    GqlError,
    type GqlFetchOptions as GenericGqlFetchOptions,
    type GqlFetchPhase,
    type GqlFetchSource,
    type GqlRequestInit,
    invalidateGqlCache as invalidateGqlCacheGeneric,
    operationNameOf,
    readGqlCache as readGqlCacheGeneric,
    runGqlFetch,
    type SanitizedGql,
} from '@amzn/bf-gql'
import { getCacheManager } from '@/core/offlineCacheManager'
import { debug } from '@/core/debug'
import { getDeviceConfig } from '@/device/deviceConfig'
import {
    type AuthRecoveryKind,
    authRecoveryKindOf,
    describeAuthRejection,
} from '@/service/gqlErrors'
import { ctx } from '@/util/context'
import { blockIdleQueue } from '@/util/idleQueue'
import { instrument } from '@/perf/instrument'
import { beginTimingSpan } from '@/perf/loadTiming'
import {
    APP_TIMINGS_GROUP,
    GQL_AUTH_LANE,
    GQL_QUERIES_LANE,
    getPerformance,
    trackTimingLane,
} from '@/perf/performanceApi'
import { artificialLatencySeconds$ } from '@/debug/featureFlagOverrides'
import { sleep } from '@amzn/bf-playback'
import { noCache } from '@/core/noCache'
import { apqDisabled } from '@/core/disableApq'
import { getGqlOrigin } from '@/service/useLiveHost'

//
// Amazon's API from following the https://dashboard.music.amazon.dev/quick-start
//
// The application-independent fetch pipeline (APQ, retry, SWR caching, sanitize,
// GqlError, cache-key derivation) lives in `@amzn/bf-gql`. This module is the
// Amu-specific adapter: it owns the Firefly wire contract (api keys, CSRF,
// bearer envelope, device ids, feature flags), the endpoint origin, perf
// instrumentation, and the idle-queue gate, and hands those to the generic
// pipeline per call.
//

const { target } = ctx('gql')

// GqlError is defined in the generic package; re-export so the app's error
// handlers keep importing it from here.
export { GqlError }

/** Firefly API keys — identify the calling client to the gateway. */
export const FireflyApiKeys = {
    FIREFLY_IOS_API_KEY: 'amzn1.application.35feb30a280642f7a70e10bea35cda76',
    FIREFLY_ANDROID_API_KEY: 'amzn1.application.e6e1516750ea4b1c8f8820ed9dc848cf',
    // GH's own provisioned anon key — the only client-side anon key GH ships.
    FIREFLY_ANONYMOUS_DEBUG_API_KEY: 'amzn1.application.0358fbc02a0f449698f433a15e1f8712',
    FIREFLY_WEB_API_KEY: 'amzn1.application.e1dc16675f9f4c78b31927d5bfd5c229',
    FIREFLY_GH_WEB_API_KEY: 'amzn1.application.4ff5579ca2e3407aba989a1f5dbdaf69',
    FIREFLY_BLACKBOLT_WEB_API_KEY: 'amzn1.application.16a6b11ba98c494a8094067ed385f83b',
    HERMES_API_KEY: 'amzn1.application.587c922830ba47549db8187301736c58',
}

export const DEFAULT_FEATURE_FLAGS = 'use-ups,api-cache'

export type GqlFetchOptions<Result = unknown> = GenericGqlFetchOptions<Result> & {
    /**
     * Override the label derived from the query — used for BOTH the
     * `gqlFetch_<name>` perf span (in {@link GQL_QUERIES_LANE}) and the
     * `__label` network-panel tag. NOT sent on the wire. Lets calls sharing
     * one query shape (e.g. a paged fetch) name each window distinctly.
     */
    readonly label?: GenericGqlFetchOptions['label']

    /**
     * Override the `x-amzn-feature-flags` header sent to Firefly. When the
     * server-side `api-cache` flag would mask freshly-written data (e.g. on
     * the read that follows a mutation), pass `'use-ups'` to opt out.
     */
    readonly featureFlags?: string

    /**
     * Attribute this request's timing bar to a track's load-journey lane, so a
     * fetch made during a track's load nests with that track's playback-delay
     * span in the Performance panel. Pass the track/song the request belongs to;
     * omit it and the bar lands in the shared timings group's default lane. A
     * function is evaluated at draw time with the resolved response — used by
     * createQueue to name its lane after the track the queue actually resolves
     * to (known only post-response), so it shares the resolved track's lane
     * rather than the clicked container's title.
     */
    readonly timingTrack?: string | ((result: unknown) => string | undefined)

    /**
     * Caller perf hook: receives per-phase span boundaries from the pipeline.
     * Chains with the service's own GraphQL-lane bars so an above-the-fold
     * caller can break its contributor into `<name> · auth` / `<name> · fetch`
     * sub-bars without the service having to know about the ATF lane.
     */
    readonly beginPhase?: (phase: GqlFetchPhase) => (source?: GqlFetchSource) => void
}

// Composite phase hook: forwards each phase boundary to the service's own
// `GraphQL auth` / `GraphQL queries` lane bar AND an optional caller hook
// (ATF). The service bar's label carries the fetch source so a DevTools reader
// sees `<op> fetch · cache` vs `<op> fetch · network` at a glance.
function composePhaseHooks(
    label: string,
    callerBeginPhase: GqlFetchOptions['beginPhase']
): (phase: GqlFetchPhase) => (source?: GqlFetchSource) => void {
    const perf = getPerformance()
    return (phase) => {
        const start = perf.now()
        const callerDone = callerBeginPhase?.(phase)
        return (source) => {
            const barLabel =
                phase === 'fetch' && source ? `${label} fetch · ${source}` : `${label} ${phase}`
            perf.timeStamp({
                label: barLabel,
                start,
                end: Math.max(start, perf.now()),
                trackName: phase === 'auth' ? GQL_AUTH_LANE : GQL_QUERIES_LANE,
                trackGroup: APP_TIMINGS_GROUP,
                color: phase === 'auth' ? 'secondary' : source === 'cache' ? 'tertiary' : 'primary',
            })
            callerDone?.(source)
        }
    }
}

/**
 * The Firefly `x-api-key` for the current auth + amp gate — the ONE place the
 * key is decided. Async because the amp gate resolves asynchronously.
 *
 * - Anonymous: GH's provisioned anon key in both gate states (the amp gate
 *   gates signed-in surfaces, so it doesn't apply), matching GH's anon feed.
 * - Bearer (LWA/MAP/CBL): the LWA-compatible web key in both gate states — GH's
 *   web key validates only against a cookie session, so the gate can't swap it.
 * - Cookie-session (AuthPortal): amp on presents as BlackBolt with its
 *   provisioned key; amp off presents as GH's web client with GH's key.
 */
async function fireflyApiKey(): Promise<string> {
    const auth = getAuth()
    if (!auth.isSignedIn()) return FireflyApiKeys.FIREFLY_ANONYMOUS_DEBUG_API_KEY
    if (auth.usesBearer()) return FireflyApiKeys.FIREFLY_WEB_API_KEY
    return (await featureEnabled('amp'))
        ? FireflyApiKeys.FIREFLY_BLACKBOLT_WEB_API_KEY
        : FireflyApiKeys.FIREFLY_GH_WEB_API_KEY
}

// Draw a bar in the `GraphQL auth` lane for a labeled auth-subphase (e.g.
// `getAuthToken`, `fireflyApiKey`) so a slow sub-step is visible inside the
// per-op `<label> auth` bar. One-shot; a throw still draws.
async function timeAuthSubphase<T>(label: string, inner: () => Promise<T>): Promise<T> {
    const done = beginTimingSpan({ lane: GQL_AUTH_LANE, label, color: 'tertiary' })
    try {
        return await inner()
    } finally {
        done()
    }
}

/**
 * Build the outgoing request headers + credentials from the current auth and
 * device state — the whole of Bullfrog's Firefly wire contract (api keys, the
 * CSRF triple, the bearer/AmznMusic envelope, device ids, feature flags). Kept
 * in the app because it is entirely application-specific; built once per fetch
 * and handed to the generic pipeline.
 */
async function buildRequestInit(featureFlags: string | undefined): Promise<GqlRequestInit> {
    const { deviceId } = await loadDeviceInfo()
    const { deviceTypeId } = getDeviceConfig()

    const headers: Record<string, string> = {
        'content-type': 'application/json',
        'x-amzn-device-id': deviceId,
        'x-amzn-device-type': deviceTypeId,
        'x-amzn-feature-flags': featureFlags ?? DEFAULT_FEATURE_FLAGS,
        dnt: '1',
        // Cookie-session controllers (AuthPortal) authenticate the request
        // with a CSRF triple; token controllers return undefined and add
        // nothing here, so the bearer/anonymous paths stay byte-identical.
        ...(getAuth().getCsrfHeaders?.() ?? {}),
    }

    // Localize server-driven strings to the active UI locale. Firefly treats
    // Content-Language as the i18n override (it trumps Accept-Language); GH
    // sends both to the same BCP-47 value, derived from the display-language
    // choice — mirror that so a language change refetches localized content.
    const locale = getLocale().value
    if (locale && locale !== 'und') {
        headers['content-language'] = locale
        headers['accept-language'] = locale
    }

    headers['x-api-key'] = await timeAuthSubphase('fireflyApiKey', fireflyApiKey)

    if (getAuth().isSignedIn()) {
        // Bearer (LWA/MAP/CBL) auth attaches the token envelope; a bearer
        // alongside a cookie session 401s, so only the token path adds it.
        if (getAuth().usesBearer()) {
            const oAuthToken = await timeAuthSubphase('getAuthToken', getAuthToken)
            headers.authorization = oAuthToken.startsWith('Atna|')
                ? `AmznMusic ${btoa(
                      JSON.stringify({
                          access_token: oAuthToken,
                          deviceId,
                          deviceTypeId,
                      })
                  )}`
                : `Bearer ${oAuthToken}`
        }
    } else {
        headers['music-territory'] = 'US'
        headers['x-amzn-session-id'] = getAnonSessionId()
    }

    // Cookie-session controllers (AuthPortal) need `credentials: 'include'`
    // so the first-party session cookie travels; token controllers leave
    // this undefined, so the LWA/MAP/CBL request keeps the same-origin
    // default (omit the key entirely rather than send `undefined`).
    const credentials = getAuth().requestCredentials
    return credentials ? { headers, credentials } : { headers }
}

// The gql endpoint host. The `__label` tag below is scoped to it so no other
// fetch (chat/relay/DMLS/PRS) is ever touched.
const GQL_HOST_PREFIX = 'gql.music.amazon.'

/**
 * Tag the request URL with `?__label=<name>` so gql calls — which all POST to
 * one URL — are separable in the browser network panel. Scoped to the
 * gql.music.amazon.* endpoint. The app's cache key, request-dedup key, and
 * request signing are all URL-independent (op name + query sha + variables; auth
 * travels in headers). URLSearchParams handles encoding.
 */
export function withLabelParam(origin: string, label: string | undefined): string {
    // Debug-only: a per-operation query param makes each URL unique, busting the CORS preflight / CDN cache in prod.
    if (!debug || !label) return origin
    const url = new URL(origin)
    if (!url.hostname.startsWith(GQL_HOST_PREFIX)) return origin
    url.searchParams.set('__label', label)
    return url.toString()
}

/**
 * Re-derive the cache key a `gqlFetch({ useCache: true })` call cached under.
 * Thin re-export of the generic derivation so app callers keep their import.
 */
export function deriveGqlCacheKey<Result, Variables>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    headers?: GqlFetchOptions['headers']
): Promise<string> {
    return deriveGqlCacheKeyGeneric(query, variables, headers)
}

const authRecoveryRef = globalRef(() => data<AuthRecoveryKind | null>(null))

/**
 * The recovery prompt raised by the first Firefly auth rejection, or null.
 * Latched: a rejected session fails every in-flight operation at once, so the
 * first report wins, and every later fetch rejects silently without reaching
 * the network until {@link resolveAuthRecovery}.
 */
export function getAuthRecovery(): ObservableValue<AuthRecoveryKind | null> {
    return authRecoveryRef.value
}

/** Releases the latch once the customer has chosen a recovery action. */
export function resolveAuthRecovery(): void {
    authRecoveryRef.value.value = null
}

function reportAuthFailure(err: unknown, label: string | undefined): boolean {
    const kind = authRecoveryKindOf(err)
    if (!kind || !(err instanceof GqlError)) return false
    if (authRecoveryRef.value.value) return true
    logWarn(
        target,
        `Auth failure, prompting for recovery: kind=${kind} operation=${label ?? 'unknown'} ${describeAuthRejection(err)}`
    )
    authRecoveryRef.value.value = kind
    return true
}

function silentError(err: unknown): ReportableError {
    return new ReportableError(
        err instanceof Error ? err.message : String(err),
        undefined,
        ErrorLevel.SILENT
    )
}

export function gqlFetch<Result = any, Variables = any>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    options?: GqlFetchOptions<Result>
): Promise<SanitizedGql<Result>> {
    // The label is derived here only for the perf span and URL tag; the
    // generic pipeline re-derives (or takes the same override) internally from
    // `options.label` for the cache key.
    const label = options?.label ?? operationNameOf(print(query))
    if (!label) logWarn(target, 'Could not infer operation name', query)
    logDebug(target, 'gqlFetch', label)
    const timingTrack = options?.timingTrack
    return instrument(
        'gqlFetch_' + label,
        async () => {
            if (authRecoveryRef.value.value) {
                throw silentError(`${label} skipped: auth recovery pending`)
            }
            // A static-render preseed reaches this path through the normal cache
            // read — the bootstrap wrote it into the cache manager (see
            // `preseedCacheManager`), so there is no fast-path branch.
            //
            // Debug-only stall knob (see `debug/featureFlagOverrides.ts`).
            // Value is in seconds; `sleep` takes seconds too.
            const debugLatencySeconds = artificialLatencySeconds$.value
            if (debugLatencySeconds > 0) {
                await sleep(debugLatencySeconds)
            }
            return blockIdleQueue(
                runGqlFetch(
                    {
                        cacheManager: getCacheManager(),
                        // Tag the URL with the label (already computed above) so
                        // gql calls are separable in the network panel.
                        origin: withLabelParam(getGqlOrigin(), label),
                        buildRequestInit: () => buildRequestInit(options?.featureFlags),
                        beginPhase: composePhaseHooks(label ?? 'anonymous', options?.beginPhase),
                    },
                    query,
                    variables,
                    // Opt in to the flyweight save: every CacheableEntity in the
                    // response is saved to the normalized cache and replaced by its
                    // canonical cached instance, so the app never holds a duplicate
                    // of an entity it has already cached.
                    { ...options, saveToNormalizedCache: true },
                    { apqDisabled, noCache }
                )
            ).catch((err: unknown) => {
                // The recovery prompt owns the failure; a page error behind it would be noise.
                if (reportAuthFailure(err, label)) throw silentError(err)
                throw err
            })
        },
        typeof timingTrack === 'function'
            ? (result: unknown) => {
                  const track = timingTrack(result)
                  return track != null ? trackTimingLane(track) : GQL_QUERIES_LANE
              }
            : timingTrack != null
              ? trackTimingLane(timingTrack)
              : GQL_QUERIES_LANE
    )()
}

/**
 * The response a `gqlFetch({ useCache: true })` call with the same query + variables +
 * headers cached, while it is under a week old, else undefined. Never reaches the network.
 */
export async function readGqlCache<Result, Variables>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    headers?: GqlFetchOptions['headers']
): Promise<SanitizedGql<Result> | undefined> {
    if (noCache) return undefined
    return readGqlCacheGeneric(
        { cacheManager: getCacheManager(), origin: getGqlOrigin() },
        query,
        variables,
        headers
    )
}

/**
 * Invalidate the cached response for a `gqlFetch({ useCache: true })` call. Pass
 * the SAME query + variables + headers the read was made with. Use this after a mutation
 * to ensure the next read returns fresh data instead of the stale cached payload.
 */
export function invalidateGqlCache<Result, Variables>(
    query: TypedDocumentNode<Result, Variables>,
    variables?: Variables,
    headers?: GqlFetchOptions['headers']
): Promise<void> {
    return invalidateGqlCacheGeneric(getCacheManager(), query, variables, headers)
}
